import React, { useState } from 'react';
import './CarePlusPauseModal.css';
import AnimatedPopup from '../../../../../components/AnimatedPopup/AnimatedPopup';
import * as translator from '../../../../../utils/translator';
import { toLocaleDateString } from '../../../../../utils/helpers';
import * as http from '../../../../../utils/http';
import { PLAN_TYPE_LABEL } from '../../CarePlusOrderDetail/carePlusConstants';
import CarePlusWordmark from '../../CarePlusOrderDetail/CarePlusWordmark';
import pauseIcon from '../../../../../icons/careplus-action-pause.svg';
import cancelIcon from '../../../../../icons/careplus-action-cancel.svg';
import successBadge from '../../../../../icons/careplus-success-badge-v2.png';
import successPauseOverlay from '../../../../../icons/careplus-success-pause-overlay-v2.png';

interface CarePlusPauseModalProps {
  subscriptionID: number;
  subscriptionHashID: string;
  planType: 'basic' | 'pro' | 'max' | 'monthly';
  isMonthlyPlan?: boolean;
  language: string;
  onClose: () => void;
  onSuccess: () => void;
  onOpenCancel: () => void;
  setStickyNotificationDetails: (details: any) => void;
}

type PauseScreen = 'options' | 'confirm';

const PAUSE_DURATIONS = [1, 2, 3];

const splitFirstWord = (text: string): [string, string] => {
  const [first, ...rest] = (text || '').split(' ');
  return [first || '', rest.join(' ')];
};

const splitLastWord = (text: string): [string, string] => {
  const words = (text || '').split(' ');
  const last = words.pop() || '';
  return [words.join(' '), last];
};

const CarePlusPauseModal: React.FC<CarePlusPauseModalProps> = ({
  subscriptionID,
  subscriptionHashID,
  planType,
  isMonthlyPlan,
  language,
  onClose,
  onSuccess,
  onOpenCancel,
  setStickyNotificationDetails,
}) => {
  const [screen, setScreen] = useState<PauseScreen>('options');
  const [selectedDuration, setSelectedDuration] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [serverResumeAt, setServerResumeAt] = useState<string>('');

  const t = (path: string) => translator.translate(language, 'my_account', path);
  const planTypeLabel = PLAN_TYPE_LABEL[planType];

  const handlePause = () => {
    setLoading(true);
    http.postToServer('account-update/subscription/care-plus/pause', { subscriptionID, durationMonths: selectedDuration }, [
      http.MIDDLEWARES.CUSTOMER_ID,
      http.MIDDLEWARES.CART_ORDER,
    ])
      .then((response: any) => {
        setServerResumeAt(response.data.pauseResumeAt);
        setScreen('confirm');
        setStickyNotificationDetails({
          show: true,
          level: 'success',
          title: t('CarePlus/pauseModal/confirmTitle'),
          content: isMonthlyPlan ? t('CarePlus/monthlyPlan/pauseConfirmBody') : (t('CarePlus/pauseModal/confirmBody') || '').replace(':tier', planTypeLabel),
        });
      })
      .catch(() => setStickyNotificationDetails({
        show: true,
        level: 'error',
        title: t('CarePlus/pauseModal/errorTitle'),
        content: t('CarePlus/pauseModal/errorBody'),
      }))
      .finally(() => setLoading(false));
  };

  const [titleAccent, titleRest] = splitFirstWord(t('CarePlus/pauseModal/title'));
  const [confirmTitleRest, confirmTitleAccent] = splitLastWord(t('CarePlus/pauseModal/confirmTitle'));
  const [bodyPrefix, bodySuffix] = (t('CarePlus/pauseModal/confirmBody') || '').split(':tier');

  return (
    <AnimatedPopup isVisible title="" onClose={screen === 'confirm' ? onSuccess : onClose} scrollable fitContent>
      {screen === 'options' ? (
        <div className="careplus-pause-modal">
          <p className="careplus-pause-modal__title">
            <span className="careplus-pause-modal__title-accent" style={{ backgroundImage: 'linear-gradient(145deg, #F59402 29.19%, #FFEDA6 158.31%)' }}>
              {titleAccent}
            </span>
            {titleRest ? ` ${titleRest}` : ''}
          </p>
          <p className="careplus-pause-modal__subtitle">{t('CarePlus/pauseModal/subtitle')}</p>

          <div className="careplus-pause-modal__options">
            {PAUSE_DURATIONS.map((n) => (
              <label key={n} className={`careplus-pause-modal__option ${selectedDuration === n ? 'careplus-pause-modal__option--selected' : ''}`}>
                <span className="careplus-pause-modal__option-radio">
                  <input
                    type="radio"
                    name="pause-duration"
                    value={n}
                    checked={selectedDuration === n}
                    onChange={() => setSelectedDuration(n)}
                  />
                </span>
                <span className="careplus-pause-modal__option-label">
                  {(t('CarePlus/pauseModal/option') || '').replace(':n', String(n))}
                </span>
              </label>
            ))}
          </div>

          <div className="careplus-pause-modal__actions">
            <button
              type="button"
              className="careplus-pause-modal__btn careplus-pause-modal__btn--pause"
              disabled={loading}
              onClick={handlePause}
            >
              <img src={pauseIcon} alt="" className="careplus-pause-modal__btn-icon" />
              {t('CarePlus/pauseModal/pause')}
            </button>
            <button
              type="button"
              className="careplus-pause-modal__link"
              onClick={() => { onClose(); onOpenCancel(); }}
            >
              <img src={cancelIcon} alt="" className="careplus-pause-modal__link-icon" />
              {t('CarePlus/pauseModal/cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="careplus-pause-modal careplus-pause-modal__confirm">
          <div className="careplus-pause-modal__badge-wrap">
            <img src={successBadge} alt="" className="careplus-pause-modal__badge" />
            <img src={successPauseOverlay} alt="" className="careplus-pause-modal__badge-overlay" />
          </div>
          <p className="careplus-pause-modal__confirm-title">
            {confirmTitleRest ? `${confirmTitleRest} ` : ''}
            <span className="careplus-pause-modal__confirm-title-accent" style={{ backgroundImage: 'linear-gradient(201deg, #FFB909 0.8%, #E17200 94.83%)' }}>
              {confirmTitleAccent}
            </span>
          </p>
          <div className="careplus-pause-modal__confirm-copy">
            <p className="careplus-pause-modal__confirm-body">
              {isMonthlyPlan ? t('CarePlus/monthlyPlan/pauseConfirmBody') : (
                <>
                  {bodyPrefix}
                  <CarePlusWordmark />
                  {' '}
                  {planTypeLabel}
                  {bodySuffix}
                </>
              )}
            </p>
            <p className="careplus-pause-modal__confirm-resubscribe">{(t('CarePlus/pauseModal/resumeDate') || '').replace(':date', serverResumeAt ? toLocaleDateString(serverResumeAt, language, { year: 'numeric' as const, month: 'long' as const, day: 'numeric' as const }) : '')}</p>
            <p className="careplus-pause-modal__confirm-resubscribe">{t('CarePlus/pauseModal/confirmPauseInfo')}</p>
            <p className="careplus-pause-modal__confirm-resubscribe">{t('CarePlus/pauseModal/confirmResubscribe')}</p>
          </div>
          <button
            type="button"
            className="careplus-pause-modal__btn careplus-pause-modal__btn--done"
            onClick={onSuccess}
          >
            {t('CarePlus/pauseModal/confirmButton')}
          </button>
        </div>
      )}
    </AnimatedPopup>
  );
};

export default CarePlusPauseModal;
